import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/esm/Button';
import Col from 'react-bootstrap/esm/Col';
import Row from 'react-bootstrap/esm/Row';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../app/store';
import LoadingIndicator from '../loading/LoadingIndicator';
import { useAuth } from '../signin/auth';
import ChecklistItemInput from './ChecklistItemInput';
import { deleteChecklist, deleteChecklistItem, getChecklist, selectChecklist, selectItems, writeChecklist } from './checklistSlice';
import EditChecklist from './EditChecklist';
import ListChecklistItems from './ListChecklistItems';
import { ChecklistUpdate } from './types';

type RouteChecklistEditParams = {
  checklistID: string;
}

const RouteChecklistEdit = () => {
  let { checklistID } = useParams<keyof RouteChecklistEditParams>() as RouteChecklistEditParams;

  const auth = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const checklist = useSelector((state: RootState) => selectChecklist(state));
  const items = useSelector((state: RootState) => selectItems(state));
  const checklistStatus = useSelector((state: RootState) => state.checklist.status)
  const checklistError = useSelector((state: RootState) => state.checklist.error)
  const [checklistState, setChecklistState] = useState<ChecklistUpdate>({ name: "" });

  const loadChecklistData = useCallback(() => {
    if (auth.authToken) {
      dispatch(getChecklist({
        authToken: auth.authToken,
        checklistID: checklistID,
        includeItems: true,
      }));
    }
  }, [dispatch, auth.authToken, checklistID]);

  useEffect(() => {
    loadChecklistData();
  }, [dispatch, auth.authToken, checklistID, loadChecklistData]);

  useEffect(() => {
    // Redirect to the list view if we can't load a checklist for whatever reason.
    if ((checklistStatus === 'succeeded' || checklistStatus === 'failed') && !checklist) {
      navigate("/checklists");
    }
  }, [checklist, navigate, checklistStatus]);

  const onSaveClicked: MouseEventHandler<HTMLButtonElement> = (evt) => {
    if (auth.authToken) {
      dispatch(writeChecklist({
        authToken: auth.authToken,
        checklist: checklistState,
      }));
    } else {
      console.log('No auth token!');
    }
  }

  const onDeleteClicked: MouseEventHandler<HTMLButtonElement> = async (evt) => {
    if (auth.authToken) {
      dispatch(deleteChecklist({
        authToken: auth.authToken,
        checklistID: checklistID,
      }));
      navigate("/checklists");
    } else {
      console.log('No auth token!');
    }
  }

  const onItemCompleteChecked = (completed: Boolean) => {
    loadChecklistData();
  }

  const onItemDeleteClicked = async (checklistItemID: string) => {
    if (auth.authToken) {
      await dispatch(deleteChecklistItem({
        authToken: auth.authToken,
        checklistID: checklistID,
        checklistItemID: checklistItemID,
      }));
      loadChecklistData();
    }
  }

  return (
    <>
      <Row className="justify-content-end">
        <Col sm="auto">
          <Link to={`/checklist/${checklistID}`}>
            <Button variant='secondary'>View</Button>
          </Link>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <div>
            {checklistStatus === 'loading' && <LoadingIndicator />}
            {checklistError && <div>{checklistError}</div>}
            {checklist &&
              <EditChecklist checklist={checklist} checklistState={checklistState} setChecklistState={setChecklistState}>
                <Row>
                  <Col className="me-auto">
                    <Button variant="primary" onClick={onSaveClicked} disabled={checklistStatus === 'loading'}>
                      Save checklist
                    </Button>
                  </Col>
                  <Col className="col-sm-auto ml-auto">
                    <Button variant="danger" onClick={onDeleteClicked} disabled={checklistStatus === 'loading'}>
                      Delete checklist
                    </Button>
                  </Col>
                </Row>
              </EditChecklist>
            }
          </div>
        </Col>
      </Row>
      {items && (
        <>
          <Row className="mt-3">
            <Col>
              <h2>Items</h2>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <ChecklistItemInput checklistID={checklistID} />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <ListChecklistItems checklistID={checklistID} items={items} onItemCompleteChecked={onItemCompleteChecked} showDelete onItemDeleteClicked={onItemDeleteClicked} />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default RouteChecklistEdit;
